import { modalController, SearchbarCustomEvent } from '@ionic/core';
import { Component, Element, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { FileDoc, SpecificationPageDoc, SpecSection, SubmittalDoc } from 'common';
import { ChangeEvent, ConcourseDb } from 'src/global/ConcourseDb';
import { FileService } from '../../global/service/FileService';
import { debounce } from 'lodash';
import { SpecificationSection } from '../../global';

@Component({
  tag: 'specification-section-viewer',
  styleUrl: 'specification-viewer.css',
})
export class SpecificationSectionViewer {
  static async present(projectId: string, description: string, specSection: SpecSection): Promise<void> {
    const modal = await modalController.create({
      component: 'specification-section-viewer',
      componentProps: { projectId, description, specSection },
      backdropDismiss: true,
      cssClass: 'concourse-modal',
    });
    modal.addEventListener('dismiss', () => {
      modal.dismiss();
    });
    await modal.present();
  }

  @Element() el: HTMLElement;
  @Prop() projectId: string;
  @Prop() description: string;
  @Prop() specSection: SpecSection;

  @Event() selected: EventEmitter<SpecSection>; // Emits the selected section id
  @Event() dismiss: EventEmitter<void>;

  @State() loading: boolean = true;
  @State() searchOpen: boolean = false;
  @State() errorMessage: string = '';

  @State() zoomPercent: number = 90;
  @State() pageId: string;
  @State() submittals: SubmittalDoc[] = [];

  private file: FileDoc;
  private pages: SpecificationPageDoc[];

  private submittalsDb: ConcourseDb;

  private debouncedLoadPagesInView = debounce(this.loadPagesInView, 500);

  loadPagesInView(containers: Element[]) {
    containers.map(container => this.loadPageInView(container));
  }
  async loadPageInView(container: Element) {
    console.log('loadPageInView', container.id);
    const pageId = container.id.replace(`page-`, '');
    const page = this.pages.find(p => p._id == pageId);
    await this.loadSVGImage(page);
  }

  async loadSVGImage(page: SpecificationPageDoc) {
    console.log(`loadSVGImage ${page._id}`);
    const pageContainer = this.el.querySelector(`#page-${page._id}`);
    const svgHolder = pageContainer.querySelector('.svg-holder') as SVGElement;
    if (!svgHolder.getAttribute('svg-loaded')) {
      svgHolder.setAttribute('svg-loaded', 'true');
      svgHolder.innerHTML = await FileService.loadSvgToString(this.projectId, page);
      svgHolder.style.backgroundColor = 'white'; // show the SVG holder
      pageContainer.querySelector('img').style.display = 'none'; // hide the thumbnail image
      console.log(`SVG loaded for page ${page._id}`);
    }
  }

  async componentWillLoad() {
    const db = FileService.getFilesDb(this.projectId);
    db.on('complete', (docs: FileDoc[]) => {
      this.parseDocs(docs, db.awaitingInitialSync);
    });
    db.on('change', async (e: ChangeEvent) => {
      this.parseDocs(await db.getDocs(), db.awaitingInitialSync);
    });
    await this.parseDocs(await db.getDocs(), db.awaitingInitialSync);
  }

  async parseDocs(docs: FileDoc[], awaitingInitialSync: boolean) {
    const file = docs.find(f => f._id == this.specSection.fileId);
    if (file) {
      this.file = file;
      this.loadSpecFile();
    } else if (!awaitingInitialSync) {
      this.loading = false;
      this.errorMessage = 'No specification file for this project';
    }
  }
  
  async loadSpecFile() {
    const db = FileService.getFilePagesDb(this.projectId, this.file._id);
    db.on('complete', (docs: SpecificationPageDoc[]) => {
      this.parsePageDocs(docs, db.awaitingInitialSync);
    });
    this.parsePageDocs(await db.getDocs(), db.awaitingInitialSync);
  }

  parsePageDocs(docs: SpecificationPageDoc[], awaitingInitialSync: boolean) {
    console.log('parsePageDocs', docs, this.specSection);
    if (docs.length > 0) {
      const firstPageIndex = docs.findIndex(p => p._id == this.specSection.firstPageId);
      const lastPageIndex = docs.findIndex(p => p._id == this.specSection.lastPageId);
      this.pages = docs.sort(FileService.sortById).slice(firstPageIndex, lastPageIndex + 1);
      this.loading = false;

      let attempts = 0;
      const maxAttempts = 10;
      const interval = setInterval(() => {
        const pageElements = this.el.querySelectorAll('.page-container');
        if (pageElements.length === this.pages.length) {
          clearInterval(interval);
          this.watchPages(pageElements);
        } else if (++attempts >= maxAttempts) {
          clearInterval(interval);
          console.warn('Failed to find all pages after 10 attempts');
        }
      }, 1000);

      this.loadSubmittals();
    } else if (!awaitingInitialSync) {
      this.loading = false;
      this.errorMessage = 'No pages for the specification file';
    }
  }

  watchPages(pageElements: NodeListOf<Element>) {
    requestAnimationFrame(() => {
      const observer = new IntersectionObserver(
        entries => {
          const visibleElements = entries
            .filter(entry => entry.isIntersecting) // Keep only visible elements
            .map(entry => entry.target); // Extract the elements themselves

          if (visibleElements.length > 0) {
            this.debouncedLoadPagesInView(visibleElements);
          }
        },
        { root: this.el.querySelector('#pagesContainer'), threshold: 0.2 },
      );

      // 2. Observe elements
      pageElements.forEach(el => observer.observe(el));
    });

    if (this.pageId) {
      this.scrollToPage(this.pageId);
    }
  }

  scrollToPage(pageId: string) {
    const image = this.el.querySelector(`#page-${pageId}`);
    if (image) {
      image.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.loadSVGImage(this.pages.find(p => p._id == pageId));
    } else {
      console.warn(`Page with ID ${pageId} not found in the viewer.`);
    }
  }

  async loadSubmittals() {
    this.submittalsDb = await ConcourseDb.getInstanceSynced(`p-${this.projectId}-submittals`);
    this.submittalsDb.on('complete', (docs: SubmittalDoc[]) => (this.submittals = docs));
    this.submittalsDb.on('change', (event: { docs: SubmittalDoc[] }) => (this.submittals = event.docs));
    this.submittals = await this.submittalsDb.getDocs();
  }

  openSearch() {
    this.searchOpen = true;

    setTimeout(() => {
      const modal = document.getElementById('search-header');
      const searchbar = modal?.querySelector('ion-searchbar') as HTMLIonSearchbarElement;
      searchbar?.componentOnReady().then(el => el.setFocus());
    }, 500); // slight delay ensures DOM is ready to focus the cursor in the search input
  }

  onSearchInput(e: SearchbarCustomEvent) {
    const searchTerm = e.detail.value.trim().toLowerCase();
  }

  selectSection(section: SpecificationSection) {
    this.searchOpen = false; // Close search when a section is selected

    setTimeout(() => {
      document.querySelector('.item.ion-color-primary')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 500); // slight delay ensures list has re-rendered before scrolling
  }

  getTitle(section: SpecificationSection) {
    const title = section.pages.find(p => !!p.title)?.title;
    if (title) return title;
    return section.pages[0].body.split('\n')[0];
  }

  clearSelections() {
    this.searchOpen = false;
  }

  render() {
    
    return (
      <Fragment>
        <ion-header>
          <ion-toolbar>
            <ion-buttons slot="start">
              <ion-button fill="clear" onClick={() => this.dismiss.emit()}>
                <ion-icon slot="icon-only" name="arrow-back" />
              </ion-button>
            </ion-buttons>
            <ion-title class="ion-no-padding">{`Specification Section ${this.specSection.num}`}</ion-title>
            <ion-buttons slot="end">
              <ion-button fill="clear" onClick={() => this.hilightParagraph()}>
                <ion-icon slot="icon-only" name="caret-down-circle"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        {this.pages && this.pages.length > 0 ? (
          <ion-content>{this.renderPages()}</ion-content>
        ) : (
          <div class="center-content" style={{ height: '100%' }}>
            <ion-spinner />
            <p>Loading {this.file?.num_pages || 0} pages...</p>
          </div>
        )}
      </Fragment>
    );
  }

  async hilightParagraph() {
    const allTspans = this.getAllTSpans(this.pages);
    const filtered = this.filterTspans(allTspans, this.description);

    filtered.forEach(res => {
      const htmlLower = res.tspan.innerHTML.toLowerCase();
      const first = res.words[0];
      const last = res.words[res.words.length - 1];
      const firstIdx = htmlLower.indexOf(first);
      const lastIdx = htmlLower.lastIndexOf(last);

      if (firstIdx === -1 || lastIdx === -1 || lastIdx < firstIdx) return;

      // Split string into 3 parts
      const html = res.tspan.innerHTML;
      const before = html.slice(0, firstIdx);
      const middle = html.slice(firstIdx, lastIdx + last.length);
      const after = html.slice(lastIdx + last.length);

      if (before.replace(/[^a-zA-Z]/g, '').length == 0 && after.replace(/[^a-zA-Z]/g, '').length == 0) {
        res.tspan.setAttribute('class', 'hilight-text');
      } else {
        res.tspan.innerHTML = `${before}<tspan class="hilight-text">${middle}</tspan>${after}`;
      }
    });

    const span = filtered[0].tspan;
    const rect = span.getBoundingClientRect();
    const isOffScreen = rect.bottom < 0 || rect.top > window.innerHeight;
    if (isOffScreen) {
      span.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setTimeout(() => {
      const svgDocs = Array.from(new Set(filtered.map(res => res.tspan.closest('svg')))).filter(s => !!s) as SVGSVGElement[];
      svgDocs.forEach(svgDoc => this.drawSearchHilights(svgDoc));
    }, 100);
  }

  getAllTSpans(pages: SpecificationPageDoc[]): SVGTSpanElement[] {
    console.log(`Get all TSpans for pages`);
    const tspans: SVGTSpanElement[] = [];
    pages.forEach(page => {
      const pageContainer = this.el.querySelector(`#page-${page._id}`);
      const svgHolder = pageContainer.querySelector('.svg-holder') as SVGElement;
      const svgDoc = svgHolder.querySelector('svg');
      if (svgDoc) {
        const pageTspans = Array.from(svgDoc.querySelectorAll('tspan'));
        tspans.push(...pageTspans);
      }
    });
    return tspans;
  }

  filterTspans(tspans: SVGTSpanElement[], paragraph: string): { tspan: SVGTSpanElement; words: string[] }[] {
    console.log(`Hilight paragraph for page`);

    const words = paragraph
      .replace(/[^a-zA-Z ]/g, ' ')
      .trim()
      .split(' ')
      .filter(w => w?.trim().length > 1)
      .map(word => word.toLowerCase());
    const largestToSmallest = words.sort((a, b) => b.length - a.length);

    const matches: { tspan: SVGTSpanElement; words: string[]; weight: number }[] = [];
    tspans.forEach((tspan: SVGTSpanElement, index) => {
      let textLower = tspan.innerHTML.toLowerCase();
      let wordsFound: string[] = [];
      largestToSmallest.forEach(word => {
        const index = textLower.indexOf(word);
        if (index > -1) {
          textLower = textLower.replace(word, '#'.repeat(word.length));
          wordsFound[index] = word;
        }
      });
      if (wordsFound.length > 0) {
        const foundLength = wordsFound.join('').length;
        matches.push({ tspan, words: wordsFound.filter(Boolean), weight: foundLength / textLower.length });
      }
    });

    const bestIndex = tspans.indexOf(matches.sort((a, b) => b.words.length - a.words.length)[0].tspan);
    const sorted = matches.sort((a, b) => {
      const wordDiff = b.words.length - a.words.length;
      if (wordDiff !== 0) return wordDiff;
      const weightDiff = b.weight - a.weight;
      if (weightDiff !== 0) return weightDiff;
      const aDist = Math.abs(tspans.indexOf(a.tspan) - bestIndex);
      const bDist = Math.abs(tspans.indexOf(b.tspan) - bestIndex);
      return aDist - bDist;
    });

    let wordsCopy = words.slice(); // Create a copy of the words array
    const bestMatches: { tspan: SVGTSpanElement; words: string[] }[] = [];
    for (const match of sorted) {
      if (match.words.every(word => wordsCopy.includes(word))) {
        match.words.forEach(word => (wordsCopy[wordsCopy.indexOf(word)] = ''));
        bestMatches.push(match);
      }
      // when all words have been accounted for in the matches, we can stop
      if (wordsCopy.filter(Boolean).length === 0) {
        return bestMatches;
      }
    }
    return bestMatches;
  }

  drawSearchHilights(svgDoc: SVGSVGElement) {
    var clientToSVG = function (svg: SVGSVGElement, clientX: number, clientY: number) {
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      return pt.matrixTransform(svg.getScreenCTM().inverse());
    };
    const tspans = svgDoc.querySelectorAll('tspan.hilight-text');
    const padding = 3; // padding around the highlight rectangle
    tspans.forEach((tspan: SVGTSpanElement) => {
      const bbox = tspan.getBoundingClientRect();
      const p1 = clientToSVG(svgDoc, bbox.left - padding, bbox.top - padding / 3);
      const p2 = clientToSVG(svgDoc, bbox.right + padding, bbox.bottom + padding / 3);

      const highlight = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      highlight.setAttribute('x', p1.x.toString());
      highlight.setAttribute('y', p1.y.toString());
      highlight.setAttribute('width', (p2.x - p1.x).toString());
      highlight.setAttribute('height', (p2.y - p1.y).toString());
      highlight.setAttribute('fill', 'red');
      highlight.setAttribute('fill-opacity', '0.3');
      highlight.setAttribute('rx', padding.toString()); // Rounded corners
      highlight.setAttribute('ry', padding.toString()); // Rounded corners
      highlight.classList.add('hilight-rect'); // Add class for easier selection
      svgDoc.insertBefore(highlight, svgDoc.firstChild);
    });
  }

  renderPages() {
    console.log('renderPages');
    return (
      <div style={{ height: '100%' }}>
        <div id="pagesContainer">
          {this.pages.map(page => {
            const ratio = page.thumb_width / page.thumb_height;
            return (
              <div id={`page-${page._id}`} class="page-container" style={{ 'width': `${this.zoomPercent}%`, 'aspect-ratio': `${ratio}` }}>
                <img src={`data:image/jpeg;base64,${page.thumb}`} style={{ width: '100%', height: '100%' }} />
                <div class="svg-holder" style={{ width: '100%', height: '100%' }} />
                <div class="page-num">Page {page._id}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
